/**
 * TripSwitcher — dropdown to flip between the trips the visitor can access
 * (their own + ones they've joined). Only rendered when the instance enables
 * multi-trip switching (feature_flags.trip_switcher) and there is more than one
 * trip to choose from — single-trip instances (e.g. wg-explore) never see it.
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useItinerary } from '../context/ItineraryContext';

export default function TripSwitcher() {
  const { t } = useTranslation();
  const { switcherEnabled, availableTrips, refreshTrips, switchTrip, title } =
    useItinerary();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Populate the list once when switching is available.
  useEffect(() => {
    if (switcherEnabled) void refreshTrips();
  }, [switcherEnabled, refreshTrips]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node))
        setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  // Nothing to switch between → don't render (keeps the header clean).
  if (!switcherEnabled || availableTrips.length <= 1) return null;

  return (
    <div className="wg-tripswitch" ref={ref}>
      <button
        type="button"
        className="wg-tripswitch__toggle"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen(o => !o)}
      >
        <span className="wg-tripswitch__current">{title}</span>
        <svg
          viewBox="0 0 24 24"
          width="14"
          height="14"
          aria-hidden
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>
      {open && (
        <ul className="wg-tripswitch__menu" role="listbox">
          {availableTrips.map(tp => (
            <li key={tp.itinerary_id}>
              <button
                type="button"
                role="option"
                aria-selected={tp.title === title}
                className="wg-tripswitch__item"
                onClick={() => {
                  setOpen(false);
                  void switchTrip(tp.itinerary_id);
                }}
              >
                <span className="wg-tripswitch__item-title">{tp.title}</span>
                <span className="wg-tripswitch__item-meta">
                  {tp.is_owner
                    ? t('collab.trip_yours')
                    : t(`collab.role_${tp.role}`)}
                  {' · '}
                  {t('collab.trip_places', { count: tp.item_count })}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
